import { faSpinner, faDownload, faChevronRight, faChevronDown, faArrowCircleRight, faArrowCircleDown, faEdit, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { push } from 'connected-react-router';
import * as React from 'react';
import { connect } from 'react-redux';
import { Button, Carousel, CarouselItem, CarouselControl, Col, Collapse, Container, Modal, ModalBody, ModalFooter, ModalHeader, Row, Form, FormGroup, Label, Input } from 'reactstrap';
import { bindActionCreators, Dispatch } from 'redux';
import { ApplicationState } from '../../../store';
import * as ConsoleStore from '../../../store/Console';
import { getAnnouncementList, addAnnouncement, updateAnnouncement, removeAnnouncement } from '../../../ApiUtils/ApiUtils';
import { UserProfileContext } from '../ConsoleLayout';
import './Announcement.css';

type AnnouncementsProps = {} & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

type Announcement = {
    id: number;
    title: string;
    body: string;
    attachmentUrl: string;
    attachmentType: string;
    createdBy: number;
    createdAt: Date;
    updatedBy: number;
};

type AnnouncementsState = {
    announcementList: Announcement[];
    modalOpen: boolean;
    currentAnnouncement: Announcement;
    isViewing: boolean;
    isEditing: boolean;
    isAdmin: boolean;
    currentPage: number;
    modalPage: number;
    aspUserId: number;
    isAnnouncementOpen: boolean;
    showDeleteConfirm: boolean;
    formErrors: {
        title?: string;
        body?: string;
        attachment?: string;
    };
    carouselActiveIndex: number;
    carouselAnimating: boolean;
    selectedImage: string | null;
    isImageViewerOpen: boolean;
};

type indicatorRefsType = indicatorRefType[];
type indicatorRefType = React.RefObject<HTMLButtonElement>
type indicatorsContainerRefType = React.RefObject<HTMLDivElement>;

const formatDate = (date: string | Date) => {
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric"
    });
};

class Announcements extends React.Component<AnnouncementsProps, AnnouncementsState> {
    static contextType = UserProfileContext;
    private indicatorRefs: indicatorRefsType = [];
    private indicatorsContainerRef: indicatorsContainerRefType = React.createRef();

    constructor(props: AnnouncementsProps) {
        super(props);
        this.state = {
            announcementList: [],
            modalOpen: false,
            currentAnnouncement: { id: 0, title: '', body: '', attachmentType: '', attachmentUrl: '', createdAt: new Date(), createdBy: 0, updatedBy: 0 },
            isViewing: false,
            isEditing: false,
            isAdmin: false,
            currentPage: 1,
            modalPage: 1,
            aspUserId: 0,
            isAnnouncementOpen: true,
            showDeleteConfirm: false,
            formErrors: {
                title: '',
                body: '',
                attachment: ''
            },
            carouselActiveIndex: 0,
            carouselAnimating: false,
            selectedImage: null,
            isImageViewerOpen: false
        };
    }

    componentDidMount() {
        getAnnouncementList().then((response) => {
            const sortedAnnouncements = response.data.announcement
                .sort((a: { createdAt: Date; }, b: { createdAt: Date; }) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

            this.setState({
                announcementList: sortedAnnouncements,
                isAdmin: this.context.permissions.admin || this.context.permissions.root,
                aspUserId: this.context.aspUserId
            });
        }).catch(err => console.error("Error fetching announcements:", err));
    }

    componentDidUpdate() {
      if (this.state.announcementList && this.indicatorRefs.length != this.state.announcementList.length) {
        this.indicatorRefs = this.state.announcementList.map(()=> React.createRef<HTMLButtonElement>())
      }
    }

    toggleModal = () => {
        this.setState({
            modalOpen: !this.state.modalOpen,
            isViewing: false,
            isEditing: false,
            currentAnnouncement: { id: 0, title: '', body: '', attachmentType: '', attachmentUrl: '', createdAt: new Date(), createdBy: 0, updatedBy: 0 },
            modalPage: 1,
            formErrors: {
                title: '',
                body: '',
                attachment: ''
            }
        });
    };

    handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.setState({
            currentAnnouncement: {
                ...this.state.currentAnnouncement,
                [e.target.name]: e.target.name === 'createdAt' ? new Date(e.target.value) : e.target.value,
            },
        });
    };

    handlePageChange = (newPage: number) => {
        this.setState({ currentPage: newPage });
    };

    handleModalPageChange = (newPage: number) => {
        this.setState({ modalPage: newPage });
    };

    saveAnnouncement = () => {
        const { announcementList, currentAnnouncement, isEditing } = this.state;
        const { attachmentUrl, attachmentType } = currentAnnouncement;
        const formErrors: AnnouncementsState["formErrors"] = {};

        if (!currentAnnouncement.title?.trim()) {
            formErrors.title = 'Title is required.';
        }

        if (!currentAnnouncement.body?.trim()) {
            formErrors.body = 'Body is required.';
        }

        if ((attachmentUrl && !attachmentType) || (!attachmentUrl && attachmentType)) {
            formErrors.attachment = 'Both attachment URL and type must be provided, or both must be empty.';
        }

        if (Object.keys(formErrors).length > 0) {
            this.setState({ formErrors });
            return;
        }

        this.setState({ formErrors: {} });

        if (isEditing) {
            currentAnnouncement.updatedBy = this.state.aspUserId;
            updateAnnouncement(currentAnnouncement).then(() => {
                this.setState(prevState => ({
                    announcementList: prevState.announcementList.map(ann => ann.id === currentAnnouncement.id ? { ...currentAnnouncement } : ann),
                    modalOpen: false,
                }));
            }).catch(err => console.error("Error updating announcement:", err));
        } else {
            currentAnnouncement.createdBy = this.state.aspUserId;
            addAnnouncement(currentAnnouncement).then((response) => {
                this.setState(prevState => ({
                    announcementList: [response.data.announcement, ...prevState.announcementList],
                    modalOpen: false
                }));
            }).catch(err => {
                console.error("Error adding announcement:", err);
            });
        }
    };

    editAnnouncement = (announcement: Announcement) => {
        if (this.state.isAdmin) {
            this.setState({ currentAnnouncement: { ...announcement }, modalOpen: true, isEditing: true });        }
    };

    viewAnnouncement = (announcement: Announcement) => {
      this.setState({ currentAnnouncement: { ...announcement }, modalOpen: true, isEditing: false, isViewing: true });
    };

    handleDeleteConfirmed = () => {
    const { currentAnnouncement } = this.state;
    removeAnnouncement(currentAnnouncement.id)
        .then(() => {
            this.setState(prevState => ({
                announcementList: prevState.announcementList.filter(ann => ann.id !== currentAnnouncement.id),
                modalOpen: false,
                showDeleteConfirm: false
            }));
        })
        .catch(err => {
            console.error("Error deleting announcement:", err);
            this.setState({ showDeleteConfirm: false });
        });
};

    nextSlide = () => {
      if (this.state.carouselAnimating) return;
      const nextIndex = this.state.carouselActiveIndex === this.state.announcementList.length - 1 ? 0 : this.state.carouselActiveIndex + 1;
      this.setState({ carouselActiveIndex: nextIndex }, this.scrollToActive)
    };

    prevSlide = () => {
      if (this.state.carouselAnimating) return;
      const nextIndex = this.state.carouselActiveIndex === 0 ? this.state.announcementList.length - 1 : this.state.carouselActiveIndex - 1;
      this.setState({ carouselActiveIndex: nextIndex }, this.scrollToActive);
    };

    goToSlideIndex = (newIndex: number) => {
      if (this.state.carouselAnimating) return;
      this.setState({ carouselActiveIndex: newIndex }, this.scrollToActive);
    };

    scrollToActive = () => {      
      const container = this.indicatorsContainerRef.current;
      if (!container) return

      const activeIndicator = this.indicatorRefs[this.state.carouselActiveIndex].current;
      if (!activeIndicator) return

      container.style.left = `calc(50% - ${activeIndicator.offsetLeft + activeIndicator.offsetWidth/2}px)`
    }

    getFileExtension = (str:string): string => {
      try {
        const lastSegment: string = str.split('/').pop() ?? ''

        if (lastSegment.includes('.') && !lastSegment.endsWith('.')) {
          return lastSegment.split('.').pop()!.toLocaleLowerCase()
        } 
      } catch (e) {
        console.error('Invalid URL:', e)
      }
      
      return ''
    }

    isIndicatorVisible = (index: number): boolean => {
      return index > this.state.carouselActiveIndex -3 && index < this.state.carouselActiveIndex + 3;
    };

    handleImageClick = (imgSrc: string) => {
      this.setState({ selectedImage: imgSrc, isImageViewerOpen: true });
    };

    toggleImageViewerModal = () => {
      this.setState((prevState) => ({
        isImageViewerOpen: !prevState.isImageViewerOpen,
      }));
    };
  
    announcementHeaderText = () => {
      if (this.state.isEditing) {
          return "Edit Announcement";
      } else if (this.state.isViewing) {
          return "View Announcement";
      } else {
          return "Add Announcement";
      }
    }

    toggleAnnouncement = () => {
      this.setState({isAnnouncementOpen: !this.state.isAnnouncementOpen})
    }

    dateInWords = (date: string | Date) => {
      const today = new Date();
      today.setHours(0,0,0,0);

      const targetDate = new Date(date);
      targetDate.setHours(0,0,0,0);

      const msPerDay = 24 * 60 * 60 * 1000;
      const days = Math.floor(Math.abs(today.getTime() - targetDate.getTime()) / msPerDay);
      const weeks = (days / 7) | 0; 
      return (weeks >= 1) ? weeks + " week(s) ago" : days + " day(s) ago";
    }

    public render() {
        const excerptLength = 500;
        const indicatorSize = 30 //in px
        const indicatorMargin = 3 //in px
        const initOffset = `calc(50% - ${indicatorMargin + (indicatorSize/2)}px)`

        return (
            <>
                <Container className="mw-100" style={{padding: "0"}}>
                  <Row className='m-1 mb-2 titleHeader'>
                        <Col className="title" style={{ display: "flex" }}>
                      <h5 className='mt-2'>Latest Announcement</h5>
                      {this.state.isAdmin && (
                        <Button className="btn btn-primary quickSightButtons addButton" onClick={this.toggleModal}>+ Add</Button>
                      )}
                      {this.state.announcementList.length > 0 && (
                        <FontAwesomeIcon className="mt-1 btnCollapsible" onClick={this.toggleAnnouncement} icon={ this.state.isAnnouncementOpen ? faArrowCircleDown : faArrowCircleRight } size="2x" style={{marginLeft:"auto", cursor:"pointer"}}/>
                      )}
                    </Col>
                   { this.state.isAdmin && (<strong className='d-flex ' style={{background:"#ccc", padding:"8px 12px", fontSize: "0.8em"}}>Reminder: Only the marketing administrators will be allowed to manage announcements</strong>)}
                  </Row>
                  <Collapse isOpen={this.state.isAnnouncementOpen}>
                    <Container className="sub mw-100" style={{ padding: 0, border: "1px solid #ccc", borderRadius: "0 0 3px 3px"}}>
                        {this.props.console?.applicationsState.isLoading ? (
                            <section className="text-center">
                                <FontAwesomeIcon icon={faSpinner} size="2x" spin={true} />
                            </section>
                        ) : (this.state.announcementList.length > 0 && (
                              <>
                                  <Carousel interval={false} activeIndex={this.state.carouselActiveIndex} next={this.nextSlide} previous={this.prevSlide}>
                                    <div className="carousel-indicators-wrapper">
                                      <div className="carousel-indicators-list" ref={this.indicatorsContainerRef} style={{left:`${initOffset}`}}>
                                        {this.state.announcementList.map((item, index) => {
                                          const isActive = this.state.carouselActiveIndex === index;
                                          return (
                                            <button
                                              key={`btn-${index}`}
                                              ref={this.indicatorRefs[index]}
                                              className={`indicator-btn ${isActive ? 'active' : ''}`}
                                              onClick={() => this.goToSlideIndex(index)}
                                              aria-label={`Go to slide ${index + 1}`}
                                              style={{display: this.isIndicatorVisible(index) ? "block" : "none", height:`${indicatorSize}px`, width:`${indicatorSize}px`}}
                                            />
                                          );
                                        })}
                                      </div>
                                    </div>
                                    {this.state.announcementList.length > 0 && this.state.announcementList.map((announcement: any, index: number) => (
                                      <CarouselItem
                                        key={index}
                                        onExiting={() => this.setState({ carouselAnimating: true })}
                                        onExited={() => this.setState({ carouselAnimating: false })}
                                        >
                                          <Row style={{margin: "0 auto", padding: "40px 0", width: "80%"}}>
                                            {announcement.attachmentUrl && (
                                            <Col style={{width:"50%"}}>
                                              <div style={{height:"100%"}}>
                                              {announcement.attachmentType == 'image' && (
                                                <img src={announcement.attachmentUrl} alt={announcement.title} onClick={() => this.handleImageClick(announcement.attachmentUrl)} style={{cursor: 'pointer'}} />
                                              )}
                                              {announcement.attachmentType == 'video' && (
                                                <video controls>
                                                  <source src={announcement.attachmentUrl} type={(['webm','ogg'].includes(this.getFileExtension(announcement.attachmentUrl)) ? `video/${this.getFileExtension(announcement.attachmentUrl)}` : 'video/mp4')} />
                                                  Your browser does not support the video tag.
                                                </video>
                                              )}
                                              {announcement.attachmentType == 'file' && this.getFileExtension(announcement.attachmentUrl) == 'pdf' && (
                                                <>
                                                <object data={`${announcement.attachmentUrl}?#view=FitH&scrollbar=0&toolbar=0&navpanes=0`} type="application/pdf" style={{ width: '100%'}}>
                                                  <iframe src={`${announcement.attachmentUrl}?#view=FitH&scrollbar=0&toolbar=0&navpanes=0`} scrolling="no" style={{ flex:1, width: '100%', height: "100%", border: '1px solid #333'}}>
                                                    <p>Your browser does not support PDFs..</p>
                                                  </iframe>
                                                </object>
                                                <a href={announcement.attachmentUrl} target="_blank" rel="noopener noreferrer" style={{fontSize:"0.8em"}}>Click here to view the PDF on a new tab</a>
                                                </>
                                              )}
                                              </div>
                                            </Col>
                                            )}
                                            <Col style={{width: announcement.attachmentUrl ? "50%" : "100%"}}>
                                              <div>
                                                <div><b style={{textTransform:"capitalize"}}>{announcement.title}</b><br/><em style={{fontSize:"0.8em"}}>{this.state.announcementList[0] ? formatDate(announcement.createdAt) : this.dateInWords(announcement.createdAt)}</em>
                                                    {this.state.isAdmin && (
                                                      <div style={{ display: "inline-block" }}>
                                                        <Button color="link" style={{ padding: "0 0.75rem" }} onClick={() => this.editAnnouncement(announcement)}>
                                                            <FontAwesomeIcon style={{ height: "1.5rem" }} icon={faEdit} />
                                                        </Button>
                                                      </div>
                                                    )}
                                                </div>
                                                <div style={{ color:"#595959", fontSize:"0.8em", textAlign:"justify" }}>{announcement.body.length > excerptLength
                                                      ? announcement.body.substring(0, excerptLength) + "..."
                                                      : announcement.body}
                                                      {announcement.body.length > excerptLength &&
                                                          <Button color="link" style={{ padding: "3px" }} onClick={() => this.viewAnnouncement(announcement)}>view more</Button>
                                                      }
                                                </div>
                                              </div>
                                            </Col>
                                          </Row>
                                      </CarouselItem>
                                    ))}

                                    <CarouselControl
                                      direction="prev"
                                      directionText="Previous"
                                      onClickHandler={this.prevSlide}
                                    />
                                    <CarouselControl
                                      direction="next"
                                      directionText="Next"
                                      onClickHandler={this.nextSlide}
                                    />
                                  </Carousel>
                              </>
                        ))}
                    </Container>
                  </Collapse>
                </Container>

                <Modal isOpen={this.state.modalOpen} toggle={this.toggleModal}>
                    <ModalHeader toggle={this.toggleModal}>{this.announcementHeaderText()}</ModalHeader>
                    <ModalBody>
                        <Form>
                            <FormGroup>
                                <Label for="title">Title</Label>
                                <Input type="text" name="title" disabled={this.state.isViewing} value={this.state.currentAnnouncement.title} onChange={this.handleInputChange} required/>
                                {this.state.formErrors.title && (
                                    <div style={{ color: 'red', fontSize: '0.75em' }}>{this.state.formErrors.title}</div>
                                )}
                            </FormGroup>
                            <FormGroup>
                                <Label for="body">Body</Label>
                                <Input style={{height:"25vh"}} type="textarea" name="body" disabled={this.state.isViewing} value={this.state.currentAnnouncement.body} onChange={this.handleInputChange} required/>
                                {this.state.formErrors.body && (
                                    <div style={{ color: 'red', fontSize: '0.75em' }}>{this.state.formErrors.body}</div>
                                )}
                            </FormGroup>
                            <FormGroup>
                                <Label for="attachmentType">Attachment Type</Label>
                                <Input
                                    type="select"
                                    name="attachmentType"
                                    disabled={this.state.isViewing}
                                    value={this.state.currentAnnouncement.attachmentType}
                                    onChange={this.handleInputChange}
                                >
                                    <option value="">--Select Type--</option>
                                    <option value="image">Image</option>
                                    <option value="video">Video</option>
                                    <option value="file">File</option>
                                </Input>
                            </FormGroup>
                            <FormGroup>
                                <Label for="attachmentUrl">Attachment Link</Label>
                                <Input
                                    type="text"
                                    name="attachmentUrl"
                                    disabled={this.state.isViewing}
                                    value={this.state.currentAnnouncement.attachmentUrl}
                                    onChange={this.handleInputChange}
                                />
                            </FormGroup>
                            {this.state.formErrors.attachment && (
                                <div style={{ color: 'red', fontSize: '0.75em', marginBottom: '10px' }}>
                                    {this.state.formErrors.attachment}
                                </div>
                            )}
                            <FormGroup>
                                <Label for="date">Date</Label>
                                <Input 
                                    type="text"
                                    name="createdAt"
                                    id="createdAt"
                                    readOnly={this.state.isEditing}
                                    value={formatDate(this.state.currentAnnouncement.createdAt)}
                                    disabled={true}  // Make it read-only for editing
                                    onChange={this.handleInputChange}
                                />
                            </FormGroup>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                      {!this.state.isViewing && 
                        <Button color="primary" onClick={this.saveAnnouncement}>{this.state.isEditing ? 'Save Changes' : 'Add Announcement'}</Button>}{' '}
                        <Button color="secondary" onClick={this.toggleModal}>{this.state.isViewing ? 'Close' : 'Cancel'}</Button>
                        {this.state.isEditing && (
                          <Button color="danger" onClick={() => this.setState({ showDeleteConfirm: true })}>
                          <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        )}
                    </ModalFooter>
                    
                </Modal>
                <Modal isOpen={this.state.showDeleteConfirm} toggle={() => this.setState({ showDeleteConfirm: false })}>
                <ModalHeader toggle={() => this.setState({ showDeleteConfirm: false })}>
                    Please confirm
                </ModalHeader>
                <ModalBody>
                    Are you sure you want to delete this announcement?
                </ModalBody>
                <ModalFooter>
                    <Button
                        className="modal-button delete-button"
                        onClick={this.handleDeleteConfirmed}
                    >
                        Delete
                    </Button>
                    <Button
                        className="modal-button cancel-button"
                        onClick={() => this.setState({ showDeleteConfirm: false })}
                    >
                        Cancel
                    </Button>
                    </ModalFooter>
                </Modal>
                <Modal className="fullscreen-modal" isOpen={this.state.isImageViewerOpen} toggle={this.toggleImageViewerModal}  centered>
                  <ModalHeader toggle={this.toggleImageViewerModal}> </ModalHeader>
                  <ModalBody className="modal-image-body">
                    {this.state.selectedImage && (
                      <img className="fullscreen-image" src={this.state.selectedImage} alt="Full View" width="100%"/>
                    )}
                  </ModalBody>
                </Modal>
            </>
        );
    }
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        console: state.console,
        router: state.router
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
        push: (path: string) => dispatch(push(path)),
        consoleActions: bindActionCreators(ConsoleStore.actionCreators, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Announcements);
